import { ReactNode } from 'react'
import {
  FormDataConsumer as RaFormDataConsumer,
  useOptionalSourceContext,
  type FormDataConsumerRenderParams,
} from 'react-admin'

/**
 * react-admin's <FormDataConsumer>, with the `getSource` render parameter that
 * v4 removed put back.
 *
 * v3 handed the render prop a `getSource` that turned a scoped name into the
 * full path of the current <ArrayInput> row, and this app leans on it in seven
 * views. v4 replaced it with a SourceContext that inputs read for themselves —
 * which covers plain inputs, but not the two components here that *parse* the
 * full path (<AutocompleteTextInput> derives its GraphQL `distinct_on` from it,
 * <RelatedCustomer> pulls the row index out of it).
 *
 * Without this, every `getSource(...)` call was `undefined(...)`: an <ArrayInput>
 * row that actually had data threw "getSource is not a function" and took the
 * whole page down. It went unnoticed because empty arrays render no rows, and a
 * fresh development database has no emails, phones or boat sections at all.
 *
 * Note the notation change — v3 produced `emails[0].type`, the SourceContext
 * produces `emails.0.type`. Consumers that pick the path apart accept both.
 */
export const FormDataConsumer = ({ children, ...props }: FormDataConsumerProps) => {
  // Outside an <ArrayInput> row there is no context and no prefix to add.
  const sourceContext = useOptionalSourceContext()
  const getSource = (source: string) => (sourceContext ? sourceContext.getSource(source) : source)

  return (
    <RaFormDataConsumer {...props}>
      {(params: FormDataConsumerRenderParams) => children({ ...params, getSource })}
    </RaFormDataConsumer>
  )
}

export interface FormDataConsumerRenderParamsWithSource extends FormDataConsumerRenderParams {
  /** Scoped name -> full form path, e.g. 'type' -> 'emails.0.type'. */
  getSource: (source: string) => string
}

export interface FormDataConsumerProps {
  children: (params: FormDataConsumerRenderParamsWithSource) => ReactNode
  [key: string]: any
}

export default FormDataConsumer
